import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types'; // Importing PropTypes

import Img from '../../../../components/Img';
import UTRModal from '../../../../components/Modal';
import PerkModalContent from './PerkModalContent';
import { trackEvent } from '../../../../components/Analytics/TrackedClick';
import { TrackingData } from '../constants';
import useStartPowerCheckout from '../../../../components/hooks/useStartPowerCheckout';
import { userParams, titles } from '../../../../components/PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import { showLoginPopUp } from '../../../../actions/login-popup-actions';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../../../components/Analytics/constants';

import styles from './cardGrid.scss';

const Card = (props) => {
    const { title, description, imageUrl, isPowered, region, content, terms, link, code, method, isOpen, clearOpenCard, cardButtonText, ctaVerb, mobileCtaText, 'data-testid': dataTestId } = props;
    const [showModal, setShowModal] = useState(isOpen);
    const dispatch = useDispatch();
    const { startPowerCheckout } = useStartPowerCheckout();
    const { SUBSCRIBER_TYPE_POWER, SUBSCRIBER_TYPE_FREE, TITLE, ACTION, PAGE, BUTTON_START_POWER_TO } = TrackingData;
    const user = useSelector(({ auth }) => auth?.user);

    useEffect(() => {
        if (showModal !== isOpen) {
            setShowModal(isOpen);
        }
    }, [isOpen]);

    const handleDetailsClick = () => {
        if (isPowered) {
           trackEvent(TITLE, {
            page: PAGE,
            partner: title,
            button: cardButtonText,
            action: ACTION,
            subscriberType: SUBSCRIBER_TYPE_POWER,
           });
        } else {
           trackEvent(TITLE, {
            page: PAGE,
            partner: title,
            button: cardButtonText,
            action: ACTION,
            subscriberType: SUBSCRIBER_TYPE_FREE,
           });
        }
        setShowModal(true);
    };

    const getDetailsClickDataAttrs = () => {
        if (isPowered) {
            return {
                'data-category': 'Power Perks',
                'data-context': `Power Perks Power Lp Redeem Perk Active ${title} Click`,
                'data-location': 'Power Perks Page - Power Lp Redeem Perk',
            };
        }
        return {
            'data-category': 'Power Upgrade',
            'data-context': `Power Perks Power Lp Redeem Perk Upgrade ${title} Click`,
            'data-location': 'Power Perks Page - Power Lp Redeem Perk',
        };
    };

    const handleCloseClick = () => {
        setShowModal(false);
        clearOpenCard();
    };

    const handlePowerClick = () => {
        if (!user) {
            const userParamsNeeded = [
                userParams.CLAIMED_PROFILE,
            ];
            const popupData = {
                userParamsNeeded,
                titles: titles.POWER_PERKS,
                type: 'power_perks',
                showCloseIcon: true,
                closeOnClickOutside: true,
                performAction: () => startPowerCheckout({
                    surface: PowerFunnelSurfaces.POWER_PERKS_PAGE,
                    gatedFeature: PowerFunnelGatedFeatures.POWER_PERKS_CARD,
                    prismicTitle: title,
                }),
            };
            dispatch(showLoginPopUp(popupData));
            return;
        }
        // segment call - mapped to GA4
        trackEvent(TITLE, {
            page: PAGE,
            partner: title,
            button: `${BUTTON_START_POWER_TO} ${ctaVerb}`,
            action: ACTION,
            subscriberType: SUBSCRIBER_TYPE_FREE,
        });

        startPowerCheckout({
            surface: PowerFunnelSurfaces.POWER_PERKS_PAGE,
            gatedFeature: PowerFunnelGatedFeatures.POWER_PERKS_CARD,
            prismicTitle: title,
        });
    };

    return (
        <>
            <div
                className={styles.card}
                onClick={handleDetailsClick}
                {...getDetailsClickDataAttrs()}
                data-testid={dataTestId}
            >
                <div className={styles.image} style={{ backgroundImage: `url(${imageUrl})` }}>
                    {!isPowered && (
                        <div className={styles.lock}>
                            <Img src="/img/v3/icons/perks/lock 18 black.svg" />
                        </div>
                    )}
                </div>
                <div className={styles.footer}>
                    <div className={styles.title} data-testid={dataTestId && `${dataTestId}.title`}>
                        {title}
                    </div>
                    <div className={styles.bottom}>
                        <div className={styles.description} data-testid={dataTestId && `${dataTestId}.description`}>
                            {description}
                        </div>
                        <div className={`${styles.button}`}>
                            <div
                                className={`btn btn-sm btn-primary text-uppercase`}
                                data-testid={dataTestId && `${dataTestId}.cta-btn`}
                            >
                                {cardButtonText}
                            </div>
                        </div>
                        <div
                            className={`d-block d-md-none link clickable fs12`}
                            data-testid={dataTestId && `${dataTestId}.mobile-cta`}
                        >
                            {mobileCtaText}
                        </div>
                    </div>
                </div>
            </div>
            {showModal && (
                <UTRModal
                    className={`modal-md`}
                    onCloseClick={handleCloseClick}
                    config={{
                        noModalBodyPadding: false,
                        modalBodyPaddingClassName: 'pl24 pr24 pt0',
                    }}
                    scrollable={true}
                    data-testid={dataTestId && `${dataTestId}.modal`}
                >
                    <PerkModalContent
                        title={title}
                        description={description}
                        region={region}
                        content={content}
                        terms={terms}
                        link={link}
                        code={code}
                        method={method}
                        isPowered={isPowered}
                        hasPastDueSubscription={!!user?.hasPastDueSubscription}
                        handlePowerClick={handlePowerClick}
                        handleCloseClick={handleCloseClick}
                        ctaVerb={ctaVerb}
                        data-testid={dataTestId && `${dataTestId}.modal.content`}
                    />
                </UTRModal>
            )}
        </>
    );
};

Card.defaultProps = {
    region: '',
    content: '',
    terms: '',
    link: '',
    code: '',
    method: '',
    'data-testid': undefined,
};

// Adding PropTypes validation
Card.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    imageUrl: PropTypes.string.isRequired,
    isPowered: PropTypes.bool.isRequired,
    region: PropTypes.string,
    content: PropTypes.string,
    terms: PropTypes.string,
    link: PropTypes.string,
    code: PropTypes.string,
    method: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    clearOpenCard: PropTypes.func.isRequired,
    cardButtonText: PropTypes.string.isRequired,
    'data-testid': PropTypes.string,
};

export default Card;
